import { Button, Modal } from 'components-antd';
import { showErrorMessage, showSuccessMessage } from 'helpers';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { Icons } from './Icons';
import classNames from 'classnames';
import { verifyOfferExternalUserOtpCode, verifyShowingExternalUserOtpCode } from 'api/offers';

interface ExternalVerificationWallModalProps {
  isModalOpen: boolean;
  sendVerificationLink: () => Promise<ExternalVerificationData>;
  onClose?: () => void;
  externalToken: string | null;
  uuid?: string;
  onVerificationSuccess?: (token: string) => void;
  isExternalOffer?: boolean;
}

const CODE_LENGTH = 6;

let isMounted = false;
export const ExternalVerificationWallModal: React.FC<ExternalVerificationWallModalProps> = (
  props,
) => {
  const [emailVerificationData, setEmailVerificationData] = useState<
    undefined | null | ExternalVerificationData
  >(undefined);

  const [enteredCode, setEnteredCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [errorText, setErrorText] = useState<string | null>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  const {
    uuid,
    onClose,
    isModalOpen,
    sendVerificationLink,
    onVerificationSuccess,
    isExternalOffer,
  } = props;

  // Load info about existing verification code request once modal is open
  useEffect(() => {
    const runEffect = async () => {
      const data = await sendVerificationLink();
      setEmailVerificationData(data);
    };

    if (isModalOpen) {
      void runEffect();
    }
  }, [isModalOpen]);

  const [remainingSecondsToResendLink, setRemainingSecondsToResendLink] = useState<number | null>(
    null,
  );

  const verifyCode = async () => {
    try {
      setIsVerifying(true);

      const apiRequest = isExternalOffer
        ? verifyOfferExternalUserOtpCode
        : verifyShowingExternalUserOtpCode;

      const response = await apiRequest(uuid, {
        code: enteredCode.join(''),
      });

      const accessToken = response.data.value;

      onVerificationSuccess?.(accessToken);
    } catch (err: any) {
      let message = 'Incorrect code. Please try again.';

      message = err.response?.data?.code === 'CODE_EXPIRED' ? 'Code expired' : message;

      setErrorText(message);
    } finally {
      setIsVerifying(false);
    }
  };

  // Update timer with remaining seconds for next attempt
  useEffect(() => {
    const interval = setInterval(() => {
      const remainingSeconds = moment(emailVerificationData?.AllowLinkResendTimestamp).diff(
        moment(),
        'seconds',
      );
      setRemainingSecondsToResendLink(remainingSeconds);

      if (remainingSeconds < 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [emailVerificationData]);

  useEffect(() => {
    if (enteredCode.join('').length === CODE_LENGTH) {
      verifyCode();
    }
  }, [enteredCode]);

  const handleResendClick = async () => {
    try {
      const data = await sendVerificationLink();
      setEmailVerificationData(data);
      showSuccessMessage('Verification code sent');
    } catch (err) {
      showErrorMessage('Error occured while sending verification code');
    }
  };

  const convertSeconds = (ticks) => {
    const minutes = Math.floor(ticks / 60);
    const seconds = ticks % 60;

    const minuteText = +minutes ? `minute${minutes > 1 ? 's' : ''}` : '';
    return `${minuteText} ${seconds} seconds`;
  };

  const getResendText = () => {
    if (remainingSecondsToResendLink === null) {
      return null;
    }

    if (remainingSecondsToResendLink > 0) {
      return <span>Resend it in {convertSeconds(remainingSecondsToResendLink)} seconds </span>;
    }

    if (remainingSecondsToResendLink <= 0) {
      return (
        <span
          className={styles.resendText}
          onClick={() => {
            handleResendClick();
          }}
        >
          Resend
        </span>
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target;

    errorText && setErrorText('');
    // Ensure only one digit is allowed
    if (value.length === 1) {
      const newCode = [...enteredCode];
      newCode[index] = value;
      setEnteredCode(newCode);
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    // Disable arrow keys
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }

    if (e.key === 'Backspace' && inputRefs.current[index - 1]) {
      const newCode = [...enteredCode];
      newCode[index] = '';
      setEnteredCode(newCode);
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Backspace' && index === 0) {
      const newCode = [...enteredCode];
      newCode[index] = '';
      setEnteredCode(newCode);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault(); // Prevent the default paste action
    const pastedData = e.clipboardData.getData('text').slice(0, 6); // Take only the first 6 characters
    const otpDigits = pastedData.split('');

    const updatedOtp = [...enteredCode]; // Create a copy of the otp state
    otpDigits.forEach((digit, index) => {
      updatedOtp[index] = digit;
      if (inputRefs.current[index]) {
        inputRefs.current[index]!.value = digit; // Programmatically set the input value
      }
    });

    setEnteredCode(updatedOtp); // Update the state to reflect the pasted values
  };

  return (
    <>
      {isModalOpen && (
        <Modal
          open={isModalOpen}
          width={450}
          footer={null}
          closable={false}
          maskClosable={false}
          destroyOnClose
          title=" "
          onCancel={() => {
            onClose?.();
          }}
          className={styles.externalVerificationModal}
          closeIcon={<Icons variant={'modal-close'} />}
        >
          <div className={styles.layout}>
            <Icons variant="external-verification" />
            <h2 className={styles.stepHeader}>Verify your device</h2>
            <div className={styles.text}>
              To access this page, enter the 6-digit code we sent to your email.
            </div>

            <div className={styles.inputWrapper}>
              {enteredCode.map((code, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type={'number'}
                  value={code}
                  className={classNames(styles.input)}
                  onChange={(e) => handleChange(e, index)}
                  maxLength={1}
                  onKeyDown={(e) => handleKeyDown(e, index)} // Handle arrow keys and backspace
                  disabled={false}
                  onPaste={index === 0 ? handlePaste : undefined} // Only the first input handles paste
                />
              ))}
            </div>

            {errorText && <div className={styles.invalidCodeText}>{errorText}</div>}

            <div className={styles.resendText}>
              {emailVerificationData &&
                (!emailVerificationData.IsBlocked ? (
                  <p>Didn’t receive the code? {getResendText()}</p>
                ) : (
                  <p>
                    Didn’t receive it? Please contact{' '}
                    <a href="emailto:support@mosaik.io">support@mosaik.io</a> for further
                    assistance.
                  </p>
                ))}
            </div>

            <Button
              disabled={enteredCode.join('').length !== CODE_LENGTH || isVerifying}
              variant={'secondary'}
              className={classNames(styles.continueButton, {
                [styles.enableButton]: enteredCode.join('').length === CODE_LENGTH,
              })}
              onClick={verifyCode}
              loading={isVerifying}
            >
              Continue
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

interface ExternalVerificationData {
  AllowLinkResendTimestamp: Date;
  IsBlocked: boolean;
}
